import { FC } from "react";
import { useSelector } from "react-redux";
import { LegacyVerifyEmailOtpResponseSuccess } from "../api/types/Onboarding";
import { TLegacyUserWithAccount } from "../api/types/User";
import { APP_CONFIG } from "../constants/constants";
import {
  C_TO_TEMPERATURE_UNITS,
  C_TO_TEMPERATURE_UNITS_VALUES,
  DISTANCE_UNITS,
  DISTANCE_UNITS_OPTIONS,
  DISTANCE_UNIT_VALUES,
  ECONOMY_UNITS,
  ECONOMY_UNITS_OPTIONS,
  ECONOMY_UNITS_VALUES,
  PRESSURE_UNITS,
  PRESSURE_UNITS_OPTIONS,
  PRESSURE_UNITS_VALUES,
  SPEED_UNITS,
  SPEED_UNITS_OPTIONS,
  SPEED_UNIT_VALUES,
  TEMPRATURE_UNITS_OPTIONS,
  VOLUME_UNITS,
  VOLUME_UNITS_OPTIONS,
} from "../pages/admin-details/account/constants";
import { REPORT_FIELD_OUTPUT_TYPES } from "../pages/reports/constants";
import {
  calculateGregorianDate,
  convertDBDateToUSFormatDate_NoDateFunc,
  convertToAmPmWith24Hr_NoDateFunc,
  dateToGregorianDays,
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../utils/dateTime";
import {
  convertFeetToMetersForApi,
  convertKmphTo,
  convertMetersTo,
  convertMetersToFeet,
  convertSpeedToKmph,
  convertToMetersForApi,
  getDistanceByUnit,
  getDistanceInKms,
} from "../utils/distance";
import { convertFuelEconomy, convertToKmlForApi } from "../utils/economy";
import { localStorageKeys, useLocalStorage } from "../utils/localStorageItems";
import { convertFromKpa, convertToKpaForApi } from "../utils/pressure";
import {
  convertTemperature,
  convertToCelsiusForApi,
} from "../utils/temperature";
import {
  convertHHMMSSToSecForApi,
  convertHoursToSecForApi,
  convertSecondsToHHMMSS,
  convertSecondsToHours,
} from "../utils/time";
import { useLoggedInUserData } from "../utils/user";
import { convertLitersTo, convertToLitersForApi } from "../utils/volume";
import { useAccountTimezone } from "./timezone";
import StatusBadge from "../components/statusBadge";
import { parseStatusWithColor } from "../utils/statusColor";

export const unitConversion = (
  outputType: REPORT_FIELD_OUTPUT_TYPES,
  cellData: any,
  units: {
    distance: DISTANCE_UNIT_VALUES;
    speed: SPEED_UNITS;
    volume: VOLUME_UNITS;
    economy: ECONOMY_UNITS;
    pressure: PRESSURE_UNITS_VALUES;
    temperature: C_TO_TEMPERATURE_UNITS_VALUES;
  },
  timezoneForDateFns: string,
  convert_to_whole_number: boolean | undefined
) => {
  let isConverted = false;
  let finalData = cellData;
  switch (outputType as REPORT_FIELD_OUTPUT_TYPES) {
    case REPORT_FIELD_OUTPUT_TYPES.DATE:
      finalData = !!cellData
        ? // backend is already converting this date to the applicable timezone and timezone conversion is not needed here,
          // thus just converting DB format to US date format
          convertDBDateToUSFormatDate_NoDateFunc(cellData)
        : cellData;
      // [deprecated]
      // formattedDateTime(
      //   cellData, // disabled 'new Date(cellData),' as this takes local time into reference
      //   APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_ONLY
      // )
      // [deprecated] changing the format only, not including timezone
      // formattedDateTimeBasedOnTimezone(
      //     new Date(cellData), // expecting cellData to be YYYY-MM-DD
      //     timezoneForDateFns,
      //     APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_ONLY
      //   )
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.NORMAL_DATE:
      // The backend now formats these epochs itself; parseInt on "09/12/2026"
      // would read 9 seconds past 1970. Only convert a raw epoch.
      finalData =
        !!cellData && !isNaN(cellData)
        ? formattedDateTimeBasedOnTimezone(
            dbTimeToJsTime(parseInt(cellData)), // expecting cellData to be UTC timestamp
            timezoneForDateFns,
            APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_ONLY
          )
        : cellData;
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.TIME:
      finalData = !!cellData
        ? convertToAmPmWith24Hr_NoDateFunc(cellData) //changing the format only, not including timezone
        : // formattedDateTimeBasedOnTimezone(
          //     fromZonedTime(
          //       `${new Date().toISOString().split("T")[0]}T${cellData}`, // expecting cellData to be HH:MM:SS
          //       "GMT"
          //     ),
          //     timezoneForDateFns,
          //     APP_CONFIG.DATETIME_FORMATS.DISPLAY.TIME_ONLY
          //   )
          cellData;
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.NORMAL_TIME:
      // As NORMAL_DATE: "4:40 PM" arrives preformatted and passes through.
      finalData =
        !!cellData && !isNaN(cellData)
        ? formattedDateTimeBasedOnTimezone(
            dbTimeToJsTime(parseInt(cellData)), // expecting cellData to be UTC timestamp
            timezoneForDateFns,
            // 12-hour without seconds. Inline rather than DISPLAY.TIME_ONLY, which is
            // shared with other reports that still want seconds.
            "h:mm aa"
          )
        : cellData;
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.DATE_TIME:
      finalData =
        !!cellData && !isNaN(cellData)
          ? formattedDateTimeBasedOnTimezone(
              dbTimeToJsTime(parseInt(cellData)), // expecting cellData to be UTC timestamp
              timezoneForDateFns,
              outputType === REPORT_FIELD_OUTPUT_TYPES.TIME
                ? APP_CONFIG.DATETIME_FORMATS.DISPLAY.TIME_ONLY // "h:mm:ss aa" // using complete date time for display because database keeps values as UTC timestamp
                : outputType === REPORT_FIELD_OUTPUT_TYPES.DATE
                  ? APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_ONLY // "MM/dd/yyyy" // using complete date time for display because database keeps values as UTC timestamp
                  : APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
            )
          : cellData;
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE:
      if (!!cellData && !isNaN(cellData)) {
        finalData = calculateGregorianDate(
          cellData, // expecting cellData to be gregorian days since GREGORIAN_START_DATE (1582-10-15)
          {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }
        );
      } else finalData = "-";
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.NUMBER:
      finalData = Number(cellData);
      // At most two decimals, trailing zeros dropped: 89.1 not "89.10".
      finalData = convert_to_whole_number
        ? Math.round(finalData)
        : String(Number(finalData?.toFixed(2)));
      break;
    case REPORT_FIELD_OUTPUT_TYPES.KPH_TO_SPEED_UNITS:
      finalData = convertKmphTo(units.speed || SPEED_UNITS.KMH, cellData);
      finalData = convert_to_whole_number
        ? Math.round(finalData)
        : finalData?.toFixed(2).replace(".00", "");
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.KM_TO_DISTANCE_UNITS:
      finalData = getDistanceByUnit(
        cellData,
        units.distance?.toString() as DISTANCE_UNIT_VALUES
      );
      finalData = convert_to_whole_number
        ? Math.round(finalData)
        : finalData?.toFixed(2).replace(".00", "");
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.KM_PER_KWH_TO_ECONOMY_UNITS:
      finalData = getDistanceByUnit(
        cellData,
        units.distance?.toString() as DISTANCE_UNIT_VALUES
      );
      finalData = finalData?.toFixed(2).replace(".00", "");
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.M_TO_DISTANCE_UNITS:
      const unit =
        DISTANCE_UNITS_OPTIONS.find(
          (option) =>
            option.value ===
            (units.distance?.toString() as DISTANCE_UNIT_VALUES)
        )?.unit || DISTANCE_UNITS_OPTIONS[0].unit;
      finalData = convertMetersTo(unit, cellData);
      finalData = convert_to_whole_number
        ? Math.round(finalData)
        : finalData?.toFixed(2).replace(".00", "");
      isConverted = true;
      break;

    case REPORT_FIELD_OUTPUT_TYPES.DECI_MT_TO_DISTANCE_UNITS:
      const unitForDecimeter =
        DISTANCE_UNITS_OPTIONS.find(
          (option) =>
            option.value ===
            (units.distance?.toString() as DISTANCE_UNIT_VALUES)
        )?.unit || DISTANCE_UNITS_OPTIONS[0].unit;
      // Convert decimeters to meters first, then to target unit
      finalData = convertMetersTo(unitForDecimeter, cellData / 10);
      finalData = convert_to_whole_number
        ? Math.round(finalData)
        : finalData?.toFixed(2).replace(".00", "");
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.L_TO_VOLUME_UNITS:
      finalData = convertLitersTo(
        units.volume || VOLUME_UNITS.LITERS,
        cellData
      );
      finalData = convert_to_whole_number
        ? Math.round(finalData)
        : finalData?.toFixed(2).replace(".00", "");
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.KPL_ECONOMY_UNITS:
      finalData = convertFuelEconomy(
        units.economy?.toString() as ECONOMY_UNITS,
        cellData
      );
      finalData = convert_to_whole_number
        ? Math.round(finalData)
        : finalData?.toFixed(2).replace(".00", "");
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.KPA_TO_PRESSURE_UNITS:
      finalData = convertFromKpa(
        cellData,
        units.pressure?.toString() as PRESSURE_UNITS_VALUES
      );
      finalData = convert_to_whole_number
        ? Math.round(finalData)
        : finalData?.toFixed(2).replace(".00", "");
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.C_TO_TEMPERATURE_UNITS:
      finalData = convertTemperature(
        cellData,
        units.temperature?.toString() as C_TO_TEMPERATURE_UNITS_VALUES
      );
      finalData = convert_to_whole_number
        ? Math.round(finalData)
        : finalData?.toFixed(2).replace(".00", "");
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.SEC_TO_HOURS:
      finalData = convertSecondsToHours(cellData);
      finalData = convert_to_whole_number
        ? Math.round(finalData)
        : finalData?.toFixed(2).replace(".00", "");
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.SEC_TO_HR_MIN: {
      // Already words, e.g. "Unknown" on an unbracketed EV charge.
      if (isNaN(Number(cellData))) {
        finalData = cellData;
        isConverted = true;
        break;
      }
      const totalMinutes = Math.round(Number(cellData) / 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      finalData = hours ? `${hours} hr ${minutes} min` : `${minutes} min`;
      isConverted = true;
      break;
    }
    case REPORT_FIELD_OUTPUT_TYPES.SEC_TO_HH_MM_SS:
      finalData = convertSecondsToHHMMSS(cellData);
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.DECI_SEC_TO_HH_MM_SS:
      // Convert deciseconds to seconds first, then to HH:MM:SS
      finalData = convertSecondsToHHMMSS(cellData / 10);
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.ACTIVE_INACTIVE_DROPDOWN:
      finalData = cellData === 1 ? "Active" : "Inactive";
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.YES_NO_DROPDOWN:
      finalData = cellData === 1 ? "Yes" : "No";
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.AUDIT_CHANGE_TYPE:
      if (cellData === 10) {
        finalData = "Insert";
      } else if (cellData === 20) {
        finalData = "Update";
      } else if (cellData === 30) {
        finalData = "Delete";
      } else {
        finalData = "N/A";
      }
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.METER_TO_FEET:
      finalData = convertMetersToFeet(cellData);
      finalData = convert_to_whole_number
        ? Math.round(finalData)
        : finalData?.toFixed(2).replace(".00", "");
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.WHOLE_NUMBER:
      finalData = Math.round(cellData);
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.HEADING_KPH_TO_SPEED_UNITS:
      let [speedValue, heading] = cellData.split(" ");
      speedValue = parseFloat(speedValue);
      speedValue = convertKmphTo(units.speed || SPEED_UNITS.KMH, speedValue);
      speedValue = Number(speedValue.toFixed(2).replace(".00", "")); // Math.round(speedValue);
      finalData = speedValue === 0 ? speedValue : `${speedValue} ${heading}`; //`${speedValue} ${heading}`;
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.ASTERISK_KM_TO_DISTANCE_UNITS:
      const hasAsterisk = cellData.indexOf("*") > -1;
      const cellDataWithoutAsterisk = hasAsterisk
        ? cellData.replace("*", "").trim()
        : cellData;
      let speedValue2 = parseFloat(cellDataWithoutAsterisk);
      speedValue2 = convertKmphTo(units.speed || SPEED_UNITS.KMH, speedValue2);
      speedValue2 = Number(speedValue2.toFixed(2).replace(".00", ""));
      finalData =
        speedValue2 === 0
          ? speedValue2
          : hasAsterisk
            ? `* ${speedValue2}`
            : speedValue2;
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.STATUS_WITH_COLOR: // cellData expected to be of the form "<display text>|<foreground color hex code>|<background color hex code>"
      // finalData stays the label (callers use it as text); DisplayConvertedValue reads the
      // colours from the original value when given the output type.
      let displayPart = parseStatusWithColor(cellData).label;
      // Add custom replacement for Event
      if (displayPart.includes("Impact Event ALERT!!!!")) {
        displayPart = "Possible Impact";
      } else if (displayPart.includes("Heading_Change")) {
        displayPart = "Heading Change";
      } else if (
        displayPart?.includes("Moving") ||
        displayPart?.includes("In Motion")
      ) {
        displayPart = "Driving";
      }
      finalData = displayPart;
      isConverted = true;
      break;
    case REPORT_FIELD_OUTPUT_TYPES.COORDINATES:
      finalData = cellData;
      isConverted = false;
      break;

    case REPORT_FIELD_OUTPUT_TYPES.RATIO_TO_PERCENT:
      finalData =
        typeof cellData === "number" && !Number.isInteger(cellData)
          ? (cellData * 100).toFixed(2).replace(".00", "")
          : cellData * 100;
      isConverted = true;
      break;

    case REPORT_FIELD_OUTPUT_TYPES.TEXT: // which is default
    default:
      if (typeof finalData === "number" && !Number.isInteger(finalData)) {
        finalData = finalData.toFixed(2).replace(/\.00$/, "");
        isConverted = true;
      }
      break;
  }
  return { finalData, isConverted };
};

const getUnitByType = (
  outputType: REPORT_FIELD_OUTPUT_TYPES | undefined,
  distanceUnits?: DISTANCE_UNITS,
  speedUnit?: SPEED_UNITS,
  volumeUnit?: VOLUME_UNITS,
  economyUnits?: ECONOMY_UNITS,
  pressureUnits?: PRESSURE_UNITS,
  temperatureUnits?: C_TO_TEMPERATURE_UNITS
) => {
  switch (outputType as REPORT_FIELD_OUTPUT_TYPES) {
    case REPORT_FIELD_OUTPUT_TYPES.KPH_TO_SPEED_UNITS:
      return speedUnit;
    case REPORT_FIELD_OUTPUT_TYPES.KM_TO_DISTANCE_UNITS:
      return distanceUnits;
    case REPORT_FIELD_OUTPUT_TYPES.M_TO_DISTANCE_UNITS:
      return distanceUnits;
    case REPORT_FIELD_OUTPUT_TYPES.KM_PER_KWH_TO_ECONOMY_UNITS:
      return `${distanceUnits}/kWh`;

    case REPORT_FIELD_OUTPUT_TYPES.DECI_MT_TO_DISTANCE_UNITS:
      return distanceUnits;
    case REPORT_FIELD_OUTPUT_TYPES.L_TO_VOLUME_UNITS:
      return volumeUnit;
    case REPORT_FIELD_OUTPUT_TYPES.KPL_ECONOMY_UNITS:
      return economyUnits;
    case REPORT_FIELD_OUTPUT_TYPES.KPA_TO_PRESSURE_UNITS:
      return pressureUnits;
    case REPORT_FIELD_OUTPUT_TYPES.C_TO_TEMPERATURE_UNITS:
      return temperatureUnits;
    case REPORT_FIELD_OUTPUT_TYPES.HEADING_KPH_TO_SPEED_UNITS:
    case REPORT_FIELD_OUTPUT_TYPES.ASTERISK_KM_TO_DISTANCE_UNITS:
      return speedUnit;
    case REPORT_FIELD_OUTPUT_TYPES.RATIO_TO_PERCENT:
      return "%";
    default:
      return "";
  }
};

const useConversion = () => {
  const { getLocalStorageItem } = useLocalStorage();

  // get timezone
  const { tzForDateFns } = useAccountTimezone();

  // account units - volume
  const orgVolumeUnit = useLoggedInUserData("volumeUnits");
  const volumeUnit: VOLUME_UNITS =
    VOLUME_UNITS_OPTIONS.find((option) => option.value === orgVolumeUnit)
      ?.unit || VOLUME_UNITS_OPTIONS[0].unit;

  // account units - economy
  const orgEconomyUnit = useLoggedInUserData(
    "economyUnits"
  ) as ECONOMY_UNITS_VALUES;
  const economyUnit: ECONOMY_UNITS =
    ECONOMY_UNITS_OPTIONS.find((option) => option.value === orgEconomyUnit)
      ?.unit || ECONOMY_UNITS_OPTIONS[0].unit;

  const orgDistanceUnit = useLoggedInUserData(
    "distanceUnits"
  ) as DISTANCE_UNIT_VALUES;

  const orgSpeedUnit = useLoggedInUserData("speedUnits") as SPEED_UNIT_VALUES;
  const speedUnit: SPEED_UNITS =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnit)?.unit ||
    SPEED_UNITS_OPTIONS[0].unit;

  const loggedInUserData: LegacyVerifyEmailOtpResponseSuccess = useSelector(
    (state: any) => state.commonReducer.user
  );
  const loggedInUser: TLegacyUserWithAccount =
    getLocalStorageItem(localStorageKeys.user) ?? loggedInUserData?.user;
  const { account } = loggedInUser;
  // added blank string as default (below) to prevent errors when account units are not set
  const distanceUnits = account.distanceunits || "";
  const economyUnits = `${account.economyunits}` || "";
  const pressureUnits = account.pressureunits || "";
  const temperatureUnits = account.temperatureunits || "";

  const convertToUnit = (
    outputType: REPORT_FIELD_OUTPUT_TYPES,
    cellData: any,
    convert_to_whole_number?: boolean
  ) => {
    // if cellData is null or undefined, return empty string
    if (cellData === null || cellData === undefined)
      return {
        finalData: "",
        isConverted: false,
      };
    // else get the converted value
    return unitConversion(
      outputType as REPORT_FIELD_OUTPUT_TYPES,
      cellData,
      {
        distance: orgDistanceUnit.toString() as DISTANCE_UNIT_VALUES, // Distance units
        speed: speedUnit, // Speed unit
        volume: volumeUnit, // Volume units
        economy: economyUnit, // Economy units
        pressure: pressureUnits.toString() as PRESSURE_UNITS_VALUES, // Pressure units
        temperature:
          temperatureUnits.toString() as C_TO_TEMPERATURE_UNITS_VALUES, // Temperature units
      },
      tzForDateFns,
      convert_to_whole_number
    );
  };

  const getUnitByOutputType = (
    outputType: REPORT_FIELD_OUTPUT_TYPES | undefined
  ) => {
    const distanceUnit: DISTANCE_UNITS =
      DISTANCE_UNITS_OPTIONS.find(
        (option) =>
          option.value === (distanceUnits.toString() as DISTANCE_UNIT_VALUES)
      )?.unit || DISTANCE_UNITS_OPTIONS[0].unit;
    const economyUnit: ECONOMY_UNITS =
      (ECONOMY_UNITS_OPTIONS.find(
        (option) => option.value === economyUnits.toString()
      )?.unit as ECONOMY_UNITS) ||
      (ECONOMY_UNITS_OPTIONS[0].unit as ECONOMY_UNITS);
    const pressureUnit: PRESSURE_UNITS =
      (PRESSURE_UNITS_OPTIONS.find(
        (option) => option.value === pressureUnits.toString()
      )?.id as PRESSURE_UNITS) ||
      (PRESSURE_UNITS_OPTIONS[0].id as PRESSURE_UNITS);
    const temperatureUnit: C_TO_TEMPERATURE_UNITS =
      (TEMPRATURE_UNITS_OPTIONS.find(
        (option) => option.value === temperatureUnits.toString()
      )?.id as C_TO_TEMPERATURE_UNITS) ||
      (TEMPRATURE_UNITS_OPTIONS[0].id as C_TO_TEMPERATURE_UNITS);
    return getUnitByType(
      outputType as REPORT_FIELD_OUTPUT_TYPES,
      distanceUnit,
      speedUnit,
      volumeUnit,
      economyUnit,
      pressureUnit,
      temperatureUnit
    );
  };

  const convertValueForAPI = (
    outputType: REPORT_FIELD_OUTPUT_TYPES | undefined,
    value: string | number
  ) => {
    const unit = getUnitByOutputType(outputType);
    if (APP_CONFIG.DEBUG.ALL)
      console.log(unit, "unit", outputType, "outputType", value, "value");
    switch (outputType as REPORT_FIELD_OUTPUT_TYPES) {
      case REPORT_FIELD_OUTPUT_TYPES.TEXT:
      case REPORT_FIELD_OUTPUT_TYPES.DATE_TIME:
      case REPORT_FIELD_OUTPUT_TYPES.DATE:
      case REPORT_FIELD_OUTPUT_TYPES.TIME:
        return value;
      case REPORT_FIELD_OUTPUT_TYPES.NUMBER:
        return value;
      case REPORT_FIELD_OUTPUT_TYPES.KPH_TO_SPEED_UNITS:
        return convertSpeedToKmph(unit as SPEED_UNITS, Number(value));

      case REPORT_FIELD_OUTPUT_TYPES.KM_TO_DISTANCE_UNITS:
        return getDistanceInKms(
          Number(value),
          DISTANCE_UNIT_VALUES[unit as DISTANCE_UNITS] // unit?.toString() as DISTANCE_UNIT_VALUES
        );

      case REPORT_FIELD_OUTPUT_TYPES.M_TO_DISTANCE_UNITS:
        return convertToMetersForApi(unit as DISTANCE_UNITS, Number(value));

      case REPORT_FIELD_OUTPUT_TYPES.DECI_MT_TO_DISTANCE_UNITS:
        return (
          convertToMetersForApi(unit as DISTANCE_UNITS, Number(value)) * 10
        );

      case REPORT_FIELD_OUTPUT_TYPES.L_TO_VOLUME_UNITS:
        return convertToLitersForApi(unit as VOLUME_UNITS, Number(value));

      case REPORT_FIELD_OUTPUT_TYPES.KPL_ECONOMY_UNITS:
        return convertToKmlForApi(
          unit?.toString() as ECONOMY_UNITS,
          Number(value)
        );

      case REPORT_FIELD_OUTPUT_TYPES.KPA_TO_PRESSURE_UNITS:
        return convertToKpaForApi(
          Number(value),
          unit?.toString() as PRESSURE_UNITS_VALUES
        );

      case REPORT_FIELD_OUTPUT_TYPES.C_TO_TEMPERATURE_UNITS:
        return convertToCelsiusForApi(
          Number(value),
          unit?.toString() as C_TO_TEMPERATURE_UNITS_VALUES
        );
      case REPORT_FIELD_OUTPUT_TYPES.SEC_TO_HOURS:
        return convertHoursToSecForApi(Number(value));
      case REPORT_FIELD_OUTPUT_TYPES.SEC_TO_HH_MM_SS:
        return convertHHMMSSToSecForApi(String(value));
      case REPORT_FIELD_OUTPUT_TYPES.DECI_SEC_TO_HH_MM_SS:
        return convertHHMMSSToSecForApi(String(value)) * 10;
      case REPORT_FIELD_OUTPUT_TYPES.GREGORIAN_DATE:
        return dateToGregorianDays(new Date(value));
      case REPORT_FIELD_OUTPUT_TYPES.ACTIVE_INACTIVE_DROPDOWN:
        return `${value}`.toLowerCase() === "active" ? 1 : 0;
      case REPORT_FIELD_OUTPUT_TYPES.YES_NO_DROPDOWN:
        return `${value}`.toLowerCase() === "yes" ? 1 : 0;
      case REPORT_FIELD_OUTPUT_TYPES.AUDIT_CHANGE_TYPE:
        return `${value}`.toLowerCase() === "insert"
          ? 10
          : `${value}`.toLowerCase() === "update"
            ? 20
            : `${value}`.toLowerCase() === "delete"
              ? 30
              : null;
      case REPORT_FIELD_OUTPUT_TYPES.METER_TO_FEET:
        return Math.round(convertFeetToMetersForApi(Number(value)));
      case REPORT_FIELD_OUTPUT_TYPES.WHOLE_NUMBER:
        return value;
      case REPORT_FIELD_OUTPUT_TYPES.HEADING_KPH_TO_SPEED_UNITS:
        let [convertedSpeed, headingBack] = String(value).split(" ");
        let speed = convertSpeedToKmph(
          unit as SPEED_UNITS,
          Number(convertedSpeed)
        );
        convertedSpeed = `${speed} ${headingBack}`;
        return convertedSpeed;
      case REPORT_FIELD_OUTPUT_TYPES.ASTERISK_KM_TO_DISTANCE_UNITS:
        let hasAsterisk = String(value).indexOf("*") > -1;
        let convertedSpeedWithoutAsterisk = hasAsterisk
          ? String(value).replace("*", "").trim()
          : String(value);
        let revertedSpeed = convertSpeedToKmph(
          unit as SPEED_UNITS,
          Number(convertedSpeedWithoutAsterisk)
        );
        return hasAsterisk ? `* ${revertedSpeed}` : revertedSpeed;
      default:
        return value;
    }
  };

  const DisplayConvertedValue: FC<{
    alignment?: "left" | "center" | "right";
    convertedCellData: { finalData: any; isConverted: boolean };
    originalData: any;
    wrapperClass?: string;
    outputType?: REPORT_FIELD_OUTPUT_TYPES;
  }> = ({
    alignment = "left",
    convertedCellData,
    originalData,
    wrapperClass = "",
    outputType,
  }) => {
    const statusColors =
      outputType === REPORT_FIELD_OUTPUT_TYPES.STATUS_WITH_COLOR
        ? parseStatusWithColor(originalData)
        : undefined;
    return (
      <>
        {/* extra debugging span */}
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE &&
          !!convertedCellData.isConverted && (
            <span className="relative">
              <pre className="absolute bottom-full text-[8px] leading-[8px] text-black/50">
                {originalData}
              </pre>
            </span>
          )}
        {/* span to display report cell data with appropriate truncation */}
        <span
          className={`report-cell max-w-80 overflow-hidden whitespace-nowrap text-ellipsis ${wrapperClass} relative block text-${alignment}${alignment !== "left" ? " pe-11" : ""}`}
          title={
            !!convertedCellData.finalData &&
            typeof convertedCellData.finalData === "string"
              ? convertedCellData.finalData
              : undefined
          }
        >
          {statusColors && (statusColors.fg || statusColors.bg) ? (
            <StatusBadge
              label={`${convertedCellData.finalData}`}
              foregroundColor={statusColors.fg}
              backgroundColor={statusColors.bg}
            />
          ) : (
            `${convertedCellData.finalData}`
          )}
        </span>
      </>
    );
  };

  return {
    convertToUnit,
    getUnitByOutputType,
    DisplayConvertedValue,
    convertValueForAPI,
  };
};

export default useConversion;
